import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { authService } from '../../../services/authService';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: '',
    confermaPassword: '',
    zoneInteresse: [],
    categorieInteresse: [],
    accettoTermini: false,
    accettoPrivacy: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const zoneOptions = [
    { value: 'centro-storico', label: 'Centro Storico' },
    { value: 'corso-cairoli', label: 'Corso Cairoli' },
    { value: 'corso-cavour', label: 'Corso Cavour' },
    { value: 'collevario', label: 'Collevario' },
    { value: 'sforzacosta', label: 'Sforzacosta' },
    { value: 'piediripa', label: 'Piediripa' },
    { value: 'villa-potenza', label: 'Villa Potenza' },
    { value: 'fuori-le-mura', label: 'Fuori le Mura' }
  ];

  const categorieOptions = [
    { value: 'ristoranti', label: 'Ristoranti e Trattorie' },
    { value: 'moda', label: 'Moda e Abbigliamento' },
    { value: 'bar-caffe', label: 'Bar e Caffè' },
    { value: 'cultura', label: 'Cultura e Librerie' },
    { value: 'salute', label: 'Salute e Benessere' },
    { value: 'servizi', label: 'Servizi Professionali' },
    { value: 'alimentari', label: 'Alimentari e Gastronomia' },
    { value: 'bellezza', label: 'Bellezza e Cura della Persona' }
  ];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.length >= 12) strength += 25;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength += 25;
    if (/\d/?.test(password)) strength += 12.5;
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) strength += 12.5;
    return Math.min(strength, 100);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nome?.trim()) {
      newErrors.nome = 'Il nome è obbligatorio';
    }

    if (!formData?.cognome?.trim()) {
      newErrors.cognome = 'Il cognome è obbligatorio';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = "L'email è obbligatoria";
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Inserisci un indirizzo email valido';
    }

    if (!formData?.password) {
      newErrors.password = 'La password è obbligatoria';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'La password deve contenere almeno 8 caratteri';
    }

    if (!formData?.confermaPassword) {
      newErrors.confermaPassword = 'Conferma la password';
    } else if (formData?.password !== formData?.confermaPassword) {
      newErrors.confermaPassword = 'Le password non corrispondono';
    }

    if (!formData?.accettoTermini) {
      newErrors.accettoTermini = 'Devi accettare i termini di servizio';
    }

    if (!formData?.accettoPrivacy) {
      newErrors.accettoPrivacy = 'Devi accettare la privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setRegistrationError('');
    setRegistrationSuccess(false);

    try {
      const fullName = `${formData?.nome?.trim()} ${formData?.cognome?.trim()}`;
      
      const { data, error } = await authService?.signUp(
        formData?.email?.trim(),
        formData?.password,
        fullName,
        'cliente'
      );

      if (error) {
        throw error;
      }

      setRegistrationSuccess(true);
      
      // Redirect to login with detailed instructions
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: `✅ Registrazione completata con successo!\n\n📧 Abbiamo inviato un'email di conferma a:\n${formData?.email}\n\n⚠️ IMPORTANTE: Prima di effettuare il login, devi confermare la tua email cliccando sul link che ti abbiamo inviato.\n\n💡 Controlla anche la cartella SPAM se non trovi l'email.`,
            email: formData?.email,
            type: 'success'
          } 
        });
      }, 3000);

    } catch (error) {
      console.error('Errore durante la registrazione:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Si è verificato un errore durante la registrazione. Riprova.';
      
      if (error?.message?.includes('already registered') || error?.message?.includes('User already registered')) {
        errorMessage = 'Questo indirizzo email è già registrato. Prova ad effettuare il login o usa un\'altra email.';
      } else if (error?.message?.includes('Invalid email')) {
        errorMessage = 'Indirizzo email non valido. Controlla e riprova.';
      } else if (error?.message?.includes('Password')) {
        errorMessage = 'La password non soddisfa i requisiti di sicurezza. Usa almeno 8 caratteri.';
      } else if (error?.message) {
        errorMessage = error?.message;
      }
      
      setRegistrationError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return 'bg-error';
    if (passwordStrength < 50) return 'bg-warning';
    if (passwordStrength < 75) return 'bg-accent';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Debole';
    if (passwordStrength < 50) return 'Media';
    if (passwordStrength < 75) return 'Buona';
    return 'Forte';
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg shadow-warm p-6 md:p-8 space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Crea il tuo account
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Registrati per salvare le tue vetrine preferite e accedere a promozioni esclusive
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Nome"
            type="text"
            placeholder="Inserisci il tuo nome"
            value={formData?.nome}
            onChange={(e) => handleInputChange('nome', e?.target?.value)}
            error={errors?.nome}
            required
          />

          <Input
            label="Cognome"
            type="text"
            placeholder="Inserisci il tuo cognome"
            value={formData?.cognome}
            onChange={(e) => handleInputChange('cognome', e?.target?.value)}
            error={errors?.cognome}
            required
          />
        </div>

        <Input
          label="Email"
          type="email"
          placeholder="esempio@email.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />

        <div className="space-y-2">
          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Crea una password sicura"
              value={formData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth focus:outline-none"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          {formData?.password && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Sicurezza password:
                </span>
                <span className={`text-xs md:text-sm font-medium ${
                  passwordStrength < 50 ? 'text-error' : 'text-success'
                }`}>
                  {getPasswordStrengthText()}
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${passwordStrength}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Usa almeno 8 caratteri con lettere maiuscole, minuscole, numeri e simboli
              </p>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label="Conferma Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Ripeti la password"
            value={formData?.confermaPassword}
            onChange={(e) => handleInputChange('confermaPassword', e?.target?.value)}
            error={errors?.confermaPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth focus:outline-none"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-warm p-6 md:p-8 space-y-6">
        <div className="space-y-2">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Preferenze (Facoltativo)
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Aiutaci a personalizzare la tua esperienza
          </p>
        </div>

        <Select
          label="Zone di Interesse"
          description="Seleziona le zone di Macerata che ti interessano"
          placeholder="Scegli una o più zone"
          options={zoneOptions}
          value={formData?.zoneInteresse}
          onChange={(value) => handleInputChange('zoneInteresse', value)}
          multiple
          searchable
          clearable
        />

        <Select
          label="Categorie di Interesse"
          description="Seleziona le categorie di attività che preferisci"
          placeholder="Scegli una o più categorie"
          options={categorieOptions}
          value={formData?.categorieInteresse}
          onChange={(value) => handleInputChange('categorieInteresse', value)}
          multiple
          searchable
          clearable
        />
      </div>
      <div className="bg-card rounded-lg shadow-warm p-6 md:p-8 space-y-4">
        <div className="space-y-4">
          <Checkbox
            label="Accetto i Termini di Servizio"
            description="Ho letto e accetto i termini e le condizioni d'uso della piattaforma"
            checked={formData?.accettoTermini}
            onChange={(e) => handleInputChange('accettoTermini', e?.target?.checked)}
            error={errors?.accettoTermini}
            required
          />

          <Checkbox
            label="Accetto la Privacy Policy"
            description="Acconsento al trattamento dei miei dati personali secondo la normativa GDPR"
            checked={formData?.accettoPrivacy}
            onChange={(e) => handleInputChange('accettoPrivacy', e?.target?.checked)}
            error={errors?.accettoPrivacy}
            required
          />
        </div>

        <div className="pt-4 space-y-4">
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isSubmitting}
            iconName="UserPlus"
            iconPosition="left"
            disabled={registrationSuccess}
          >
            {isSubmitting ? 'Registrazione in corso...' : registrationSuccess ? 'Registrazione completata!' : 'Registrati'}
          </Button>

          {registrationError && (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-sm text-error flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                {registrationError}
              </p>
            </div>
          )}

          {registrationSuccess && (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <p className="text-sm text-success flex items-center gap-2">
                <Icon name="CheckCircle" size={16} />
                Registrazione completata! Reindirizzamento al login...
              </p>
            </div>
          )}

          <div className="text-center">
            <p className="text-sm md:text-base text-muted-foreground">
              Hai già un account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-primary hover:text-primary/80 font-medium transition-smooth focus:outline-none focus:underline"
              >
                Accedi qui
              </button>
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default RegistrationForm;